import {useRef} from 'react';

import {
    Alert,
    SimpleGrid,
} from '@mantine/core';
import {useNavigate} from 'react-router-dom';

import {
    MantineDataTable,
    TableSearchForm,
    DataTableTokenPagination,
} from '@flexinet/ui-components';

import {useApplications} from '../hooks/useApplications.ts';
import {useApplicationsFilter} from '../hooks/useApplicationsFilter.ts';
import {useApplicationsSelection} from '../hooks/useApplicationsSelection.ts';
import {useApplicationsSearch} from '../hooks/useApplicationsSearch.ts';
import {useVerifiedPermissions} from '../../../hooks/useVerifiedPermissions.ts';
import {Application} from '../types';

// FNP-913: migrated from a hand-rolled Mantine <Table> (TableHeaderRow +
// TableBody + SimpleTablePagination) to the shared MantineDataTable.
//
// The hand-rolled table was not broken — it rendered rows and supported
// selection — but it structurally could not have sorting, token pagination, or
// the loading/empty/error states, because all of those come from the shared
// component. Five separate checklist gaps closed by changing one component.
export const RecordsTable = () => {

    const {allowedActions} = useVerifiedPermissions();
    const navigate = useNavigate();

    const {filters} = useApplicationsFilter();

    const {
        columns,
        // The full loaded set BEFORE keyword filtering — the window the
        // footer's "N of M loaded" claim is measured against.
        items,
        visibleItems,
        sortStatus,
        setSortStatus,
        loading,
        isError,
        nextToken,
        fetchRecords,
    } = useApplications(filters);

    const {
        keyword,
        handleFilterByKeyword,
    } = useApplicationsSearch();

    const {
        selection,
        handleRowSelection,
        handleClearSelection,
    } = useApplicationsSelection();

    const scrollViewportRef = useRef<HTMLDivElement>(null);

    // Selection stays gated on DeleteApplication, exactly as the hand-rolled
    // table gated its select column — the affordance is HIDDEN, not disabled,
    // when the action is not permitted.
    const canSelect = allowedActions.includes('DeleteApplication');

    const selectedRecords = canSelect
        ? visibleItems.filter(item => selection[item.id])
        : undefined;

    return (
        <SimpleGrid verticalSpacing='xl' m='sm'>
            {/*
              * TableSearchForm, not SimpleTableBatchAction. The latter REQUIRES
              * `limit` + `handleItemsPerPageChange` and always renders the
              * rows-per-page dropdown — which token pagination cannot honour
              * (there is no page to resize and no total to divide).
              *
              * Nothing is lost: no caller ever passed `extraTools` or
              * `settings` (IndexPage renders a bare <RecordsTable/>), and the
              * search box is what TableSearchForm provides. It is also the
              * clearable one — the clear control resets BOTH the input and the
              * module-scoped keyword store.
              *
              * Page size is a server concern now: PAGE_SIZE in the query hook.
              */}
            <TableSearchForm handleFilterByKeyword={handleFilterByKeyword}/>

            {isError && (
                <Alert color='red' title='Could not load applications'>
                    The console API did not return the application catalog. Try Reload;
                    if it persists the API may be unavailable in this environment.
                </Alert>
            )}

            <MantineDataTable<Application>
                columns={columns}
                records={visibleItems}
                // Sorting is client-side over the pages loaded so far — the same
                // scope the keyword filter works on. Token pagination cannot
                // reorder rows the browser has not fetched.
                sortStatus={sortStatus}
                onSortStatusChange={setSortStatus}
                fetching={loading}
                noRecordsText={I18nNoRecords}
                selectedRecords={selectedRecords}
                onSelectedRecordsChange={canSelect
                    ? (records) => {
                        handleClearSelection();
                        records.forEach(record => handleRowSelection(record.id, true));
                    }
                    : undefined}
                onScrollToBottom={() => fetchRecords()}
                scrollViewportRef={scrollViewportRef}
                withTableBorder={true}
                onRowClick={({record}) =>
                    navigate(`/applications/${encodeURIComponent(record.id)}`)}
            />

            <DataTableTokenPagination
                loadedCount={visibleItems.length}
                unfilteredCount={items.length}
                filterTerm={keyword}
                noun={'application'}
                nextToken={nextToken}
                handleLoadMore={fetchRecords}
            />
        </SimpleGrid>
    );
};

// Explicit empty state — zero rows must never be an unexplained void.
const I18nNoRecords = 'No applications found';
